// External dependencies
import React, { useEffect, useState } from 'react';

// Internal dependencies
import { getAnnouncements } from '@/modules/cms/client/api/cms.api';

/**
 * Site-wide announcement banners.
 *
 * Dropped into the main layout, so it renders on every page. Content is
 * server sanitized on write and on read, which is what makes
 * `dangerouslySetInnerHTML` acceptable here.
 *
 * Dismissal is deliberately session-only — no storage, no user preference
 * plumbing — so a dismissed banner returns on the next page load.
 */
export default function SiteAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [dismissed, setDismissed] = useState([]);

  useEffect(() => {
    let cancelled = false;

    getAnnouncements()
      .then(data => {
        if (!cancelled) {
          setAnnouncements(data);
        }
      })
      // A banner is not worth surfacing an error over; stay silent and
      // render nothing.
      .catch(() => {
        if (!cancelled) {
          setAnnouncements([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const visible = announcements.filter(
    announcement => !dismissed.includes(announcement._id),
  );

  // The site header is `position: fixed`, so a banner in normal flow would
  // render underneath it. Mirror what the config-driven announcement does:
  // sit fixed at the top and push the header down by the same amount, via a
  // body class. Removed on unmount and when the last banner is dismissed.
  useEffect(() => {
    const CLASS_NAME = 'is-cms-announcement-visible';

    if (visible.length) {
      document.body.classList.add(CLASS_NAME);
    } else {
      document.body.classList.remove(CLASS_NAME);
    }

    return () => {
      document.body.classList.remove(CLASS_NAME);
    };
  }, [visible.length]);

  if (!visible.length) {
    return null;
  }

  return (
    <div className="site-announcements">
      {visible.map(announcement => (
        <div
          key={announcement._id}
          className={`alert alert-${announcement.variant || 'info'}`}
          role="status"
        >
          <div className="container">
            {announcement.dismissible && (
              <button
                type="button"
                className="close"
                aria-label="Dismiss"
                onClick={() =>
                  setDismissed(previous => [...previous, announcement._id])
                }
              >
                <span aria-hidden="true">&times;</span>
              </button>
            )}
            <span dangerouslySetInnerHTML={{ __html: announcement.content }} />
          </div>
        </div>
      ))}
    </div>
  );
}

SiteAnnouncements.propTypes = {};
